<template>
    <div>
        <div v-if="isLoading" class="grid grid-cols-1 gap-4">
            <div class="col-span-1 md:col-span-3">
                <div class="flex flex-col rounded-sm border border-slate-200 bg-white p-4">
                    <div class="mb-4 h-4 w-1/2 animate-pulse bg-gray-200"></div>
                    <div class="h-[400px] w-full animate-pulse bg-gray-200"></div>
                </div>
            </div>
            <div
                v-for="chart in pieCharts"
                :key="chart.key"
                class="flex flex-col rounded-sm border border-slate-200 bg-white p-4"
            >
                <div class="mb-4 h-4 w-1/2 animate-pulse bg-gray-200"></div>
                <div class="m-auto h-[280px] w-[280px] animate-pulse rounded-full bg-gray-200"></div>
            </div>
        </div>

        <div v-else-if="!aggregationData" class="text-center">
            <!-- <p class="text-lg">No data available</p> -->
        </div>

        <div v-else>
            <div class="mb-4 mt-6 border-t border-slate-200"></div>
            <div ref="keyMetricsRef" class="relative pt-5">
                <div class="mb-2 flex items-start justify-between">
                    <div>
                        <h2 class="font-semibold leading-none text-slate-800">Key Metrics</h2>
                        <p class="text-sm text-slate-500">
                            High-level performance indicators for the creators included in this report.
                        </p>
                    </div>
                    <ActionMenu>
                        <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportKeyMetrics">
                            <ImageIcon class="h-3.5 w-3.5" />
                            <span>Export as Image</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportKeyMetricsCsv">
                            <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                            <span>Export as CSV</span>
                        </DropdownMenuItem>
                    </ActionMenu>
                </div>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div
                        v-for="field in summaryMetrics"
                        :key="field.key"
                        class="rounded border border-slate-200 bg-white p-4 shadow-sm"
                    >
                        <div class="text-xs font-semibold uppercase text-slate-500">{{ field.label }}</div>
                        <div class="mt-1 text-2xl font-semibold text-slate-800">{{ field.value }}</div>
                        <p class="mt-1 text-xs text-slate-500">{{ field.description }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sponsorship Analytics Tables - positioned after Key Metrics -->
        <template v-if="isLoading || isLoadingFacets" >
             <div class="grid grid-cols-2 gap-4 mt-8 border-t border-slate-200 pt-5">
                <div v-for="index in 4" :key="index" class="">
                    <div class="flex flex-col rounded-sm border border-slate-200 bg-white p-4">
                        <div class="mb-4 h-4 w-1/2 animate-pulse bg-gray-200"></div>
                        <div class="h-[200px] w-full animate-pulse bg-gray-200"></div>
                    </div>
                </div>
             </div>
        </template>
        <template v-else>
            <SponsorshipAnalyticsTables
                v-if="sponsorshipEnabled"
                :facets="facets"
                :report-id="reportId"
                :selected-facet-dimension="resolvedFacetDimension"
                :selected-facet-key="resolvedFacetKey"
            />
        </template>

        <div
            v-if="hasTimeseriesIntervals || organicTimeseriesData || facets"
            ref="contentTimelineRef"
            class="mt-8 border-t border-slate-200 pt-5"
        >
            <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 class="text-base font-semibold text-slate-800">Content Timeline</h2>
                    <p class="text-sm text-slate-500">
                        Performance metrics over time for content included in this report.
                    </p>
                </div>
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Popover v-model:open="metricMenuOpen">
                        <PopoverTrigger as-child>
                            <Button
                                variant="outline"
                                class="h-10 min-w-[200px] justify-between gap-2 text-sm sm:min-w-[240px]"
                                :disabled="!metricOptionGroups.length"
                            >
                                <div class="flex flex-1 flex-wrap items-center gap-1.5">
                                    <template v-if="selectedMetricOptions.length === 0">
                                        <span class="text-slate-500">Select metrics</span>
                                    </template>
                                    <template v-else-if="selectedMetricOptions.length <= 2">
                                        <Badge
                                            v-for="option in selectedMetricOptions"
                                            :key="option.id"
                                            variant="secondary"
                                            class="whitespace-nowrap text-xs font-semibold uppercase tracking-wide"
                                        >
                                            {{ option.shortLabel }}
                                        </Badge>
                                    </template>
                                    <template v-else>
                                        <Badge
                                            variant="secondary"
                                            class="text-xs font-semibold uppercase tracking-wide"
                                        >
                                            {{ selectedMetricOptions.length }} selected
                                        </Badge>
                                    </template>
                                </div>
                                <ChevronDown class="h-4 w-4 shrink-0 opacity-60" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent class="w-[420px] p-0" align="end">
                            <Tabs v-model="activeMetricTab" class="w-full">
                                <div class="border-b border-slate-200 bg-slate-50">
                                    <TabsList
                                        class="inline-flex h-auto w-full justify-start rounded-none bg-transparent p-0"
                                    >
                                        <TabsTrigger
                                            v-for="group in metricOptionGroups"
                                            :key="group.id"
                                            :value="group.id"
                                            class="rounded-none border-b-2 border-transparent px-4 py-2.5 text-xs font-semibold data-[state=active]:border-indigo-500 data-[state=active]:bg-white data-[state=active]:text-indigo-600"
                                        >
                                            {{ group.label }}
                                        </TabsTrigger>
                                    </TabsList>
                                </div>
                                <Command>
                                    <CommandInput
                                        placeholder="Search metrics..."
                                        class="h-8 !border-0 p-0 text-xs !ring-0 !ring-offset-0 focus:!ring-0 focus:!ring-offset-0"
                                    />
                                    <TabsContent
                                        v-for="group in metricOptionGroups"
                                        :key="`content-${group.id}`"
                                        :value="group.id"
                                        class="mt-0"
                                    >
                                        <CommandList
                                            class="max-h-[400px] overflow-auto scrollbar scrollbar-track-slate-200 scrollbar-thumb-slate-400 scrollbar-w-2 scrollbar-h-2 hover:scrollbar-thumb-slate-500 active:scrollbar-thumb-slate-600"
                                        >
                                            <CommandEmpty class="px-3 py-4 text-sm text-slate-500"
                                                >No matching metrics.</CommandEmpty
                                            >
                                            <template
                                                v-for="(subgroup, subgroupIndex) in group.subgroups"
                                                :key="subgroup.id"
                                            >
                                                <div
                                                    class="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                                                >
                                                    {{ subgroup.label }}
                                                </div>
                                                <CommandGroup>
                                                    <CommandItem
                                                        v-for="option in subgroup.options"
                                                        :key="option.id"
                                                        :value="option.id"
                                                        class="flex items-center gap-3 text-xs"
                                                        @select="toggleMetricSelection(option.id)"
                                                    >
                                                        <div
                                                            :class="[
                                                                'mr-1 flex size-4 items-center justify-center rounded-sm border border-primary',
                                                                isMetricSelected(option.id)
                                                                    ? 'bg-primary text-primary-foreground'
                                                                    : 'opacity-50 [&_svg]:invisible',
                                                            ]"
                                                        >
                                                            <Check class="size-3" />
                                                        </div>
                                                        <span class="font-medium text-slate-900">
                                                            {{ option.metricLabel }}
                                                        </span>
                                                    </CommandItem>
                                                </CommandGroup>
                                                <CommandSeparator
                                                    v-if="subgroupIndex < group.subgroups.length - 1"
                                                    class="my-1"
                                                />
                                            </template>
                                            <CommandSeparator
                                                v-if="group.subgroups.length && selectedMetricOptions.length"
                                                class="my-2"
                                            />
                                            <CommandGroup v-if="selectedMetricOptions.length">
                                                <CommandItem
                                                    :value="`__reset__${group.id}`"
                                                    class="justify-center text-xs font-medium text-indigo-600"
                                                    @select="clearMetricSelections"
                                                >
                                                    Reset selection
                                                </CommandItem>
                                            </CommandGroup>
                                        </CommandList>
                                    </TabsContent>
                                </Command>
                            </Tabs>
                        </PopoverContent>
                    </Popover>
                    <ActionMenu>
                        <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportChartCard">
                            <ImageIcon class="h-3.5 w-3.5" />
                            <span>Export as Image</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportChartCardCsv">
                            <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                            <span>Export as CSV</span>
                        </DropdownMenuItem>
                    </ActionMenu>
                </div>
            </div>

            <div v-if="hasTimeseriesIntervals" class="mb-4 flex flex-col sm:flex-row sm:justify-end">
                <Tabs v-model="activeTab" default-value="weekly" class="w-full sm:w-auto">
                    <TabsList class="inline-flex w-full rounded-md bg-slate-200 p-1 shadow-sm sm:w-auto">
                        <TabsTrigger
                            v-for="intervalName in timeseriesIntervals"
                            :key="intervalName"
                            :value="String(intervalName)"
                            class="px-3 py-2 font-semibold capitalize leading-tight hover:text-foreground sm:px-4"
                        >
                            {{ intervalName }}
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            <div v-if="organicTimeseriesData || facets">
                <div ref="chartCardRef" class="rounded-sm border border-slate-200 bg-white p-4">
                    <div ref="chartContainer" class="chart-container h-[350px] w-full">
                        <Line
                            :key="`chart-${selectedMetricIds.join(',')}-${activeTab}`"
                            :data="viewsChartData"
                            :options="timeseriesOptions"
                        />
                    </div>
                </div>
            </div>
        </div>

        <template v-if="isLoading || isLoadingFacets">
            <div class="grid grid-cols-3 gap-4 mt-8 border-t border-slate-200 pt-5">
                <div v-for="index in 6" :key="index" class="">
                    <div class="flex flex-col rounded-sm border border-slate-200 bg-white p-4">
                        <div class="mb-4 h-4 w-1/2 animate-pulse bg-gray-200"></div>
                        <div class="h-[200px] w-full animate-pulse bg-gray-200"></div>
                    </div>
                </div>
             </div>

             <div class="grid grid-cols-3 gap-4 mt-8 border-t border-slate-200 pt-5">
                <div v-for="index in 6" :key="index" class="">
                    <div class="flex flex-col rounded-sm border border-slate-200 bg-white p-4">
                        <div class="mb-4 h-4 w-1/2 animate-pulse bg-gray-200"></div>
                        <div class="h-[200px] w-full animate-pulse bg-gray-200"></div>
                    </div>
                </div>
             </div>

        </template>
        <template v-else>

            <div ref="channelDemographicsRef" class="mt-8 border-t border-slate-200 pt-5">
                <div class="mb-2 flex items-start justify-between">
                    <div>
                        <h2 class="text-base font-semibold text-slate-800">Channel Demographics</h2>
                        <p class="text-sm text-slate-500">
                            These values describe the creator's own channel metadata (e.g., the language/country reported on
                            the account).
                        </p>
                    </div>
                    <ActionMenu>
                        <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportChannelDemographics">
                            <ImageIcon class="h-3.5 w-3.5" />
                            <span>Export as Image</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportChannelDemographicsCsv">
                            <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                            <span>Export as CSV</span>
                        </DropdownMenuItem>
                    </ActionMenu>
                </div>

                <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div
                        v-for="chart in channelPieCharts"
                        :id="`channel-chart-${chart.key}`"
                        :key="`channel-${chart.key}`"
                        class="rounded-sm border border-slate-200 bg-white p-4"
                    >
                        <div class="mb-4 flex items-start justify-between">
                            <h2 class="text-sm font-semibold">{{ chart.title }}</h2>
                            <ActionMenu v-if="hasChannelData(chart)">
                                <DropdownMenuItem
                                    class="cursor-pointer gap-2 text-xs"
                                    @click="exportIndividualChart(chart.key, chart.title, 'channel')"
                                >
                                    <ImageIcon class="h-3.5 w-3.5" />
                                    <span>Export as Image</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    class="cursor-pointer gap-2 text-xs"
                                    @click="exportIndividualChartCsv(chart.key, chart.title, 'channel')"
                                >
                                    <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                    <span>Export as CSV</span>
                                </DropdownMenuItem>
                            </ActionMenu>
                        </div>
                        <div v-if="hasChannelData(chart)">
                            <div class="chart-container h-[280px] w-full p-5">
                                <Pie :data="getChannelChartData(chart)" :options="pieOptions" />
                            </div>

                            <div class="mt-4 flex flex-col space-y-1 text-sm">
                                <template v-if="expandedChannelCharts[chart.key]">
                                    <div
                                        v-for="(value, index) in getChannelFacetValues(chart)"
                                        :key="value.key"
                                        class="group relative flex h-7"
                                    >
                                        <div
                                            class="absolute inset-0 mx-2 rounded-[2px]"
                                            :style="`width: ${value.percentage}%`"
                                            :class="`bg-${tailwindColors[index]}-100`"
                                        />
                                        <div class="relative flex w-full justify-between">
                                            <div class="flex">
                                                <div
                                                    class="mr-2 h-full w-2.5 rounded-[2px]"
                                                    :class="`bg-${tailwindColors[index]}-400`"
                                                />
                                                <div class="my-auto leading-none">
                                                    {{ chart.label ? chart.label(value.key) : value.key }}
                                                </div>
                                            </div>
                                            <div class="my-auto leading-none">
                                                {{ numeral(value.percentage / 100).format('0.0%') }}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        v-if="getChannelFacetValues(chart).length > 3"
                                        class="mt-2 font-medium text-indigo-500 hover:text-indigo-700"
                                        @click="expandedChannelCharts[chart.key] = false"
                                    >
                                        Show less
                                    </button>
                                </template>
                                <template v-else>
                                    <div
                                        v-for="(value, index) in getChannelFacetValues(chart).slice(0, 3)"
                                        :key="value.key"
                                        class="group relative flex h-7"
                                    >
                                        <div
                                            class="absolute inset-0 mx-2 rounded-[2px]"
                                            :style="`width: ${value.percentage}%`"
                                            :class="`bg-${tailwindColors[index]}-100`"
                                        />
                                        <div class="relative flex w-full justify-between">
                                            <div class="flex">
                                                <div
                                                    class="mr-2 h-full w-2.5 rounded-[2px]"
                                                    :class="`bg-${tailwindColors[index]}-400`"
                                                />
                                                <div class="my-auto font-medium leading-none">
                                                    {{ chart.label ? chart.label(value.key) : value.key }}
                                                </div>
                                            </div>
                                            <div class="my-auto leading-none">
                                                {{ numeral(value.percentage / 100).format('0.0%') }}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        v-if="getChannelFacetValues(chart).length > 3"
                                        class="mt-2 font-medium text-indigo-500 hover:text-indigo-700"
                                        @click="expandedChannelCharts[chart.key] = true"
                                    >
                                        Show more
                                    </button>
                                </template>
                            </div>
                        </div>

                        <p v-else class="py-4 text-center text-gray-500">No data available for this chart</p>
                    </div>
                </div>
            </div>

            <div ref="audienceDemographicsRef" class="mt-10 border-t border-slate-200 pt-5">
                <div class="mb-2 flex items-start justify-between">
                    <div>
                        <h2 class="text-base font-semibold text-slate-800">Audience Demographics</h2>
                        <p class="text-sm text-slate-500">
                            These values describe the audience identified by Streamforge analytics—use them to understand
                            who watches the creator.
                        </p>
                    </div>
                    <ActionMenu>
                        <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportAudienceDemographics">
                            <ImageIcon class="h-3.5 w-3.5" />
                            <span>Export as Image</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportAudienceDemographicsCsv">
                            <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                            <span>Export as CSV</span>
                        </DropdownMenuItem>
                    </ActionMenu>
                </div>

                <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div
                        v-for="chart in audiencePieCharts"
                        :id="`audience-chart-${chart.key}`"
                        :key="`audience-${chart.key}`"
                        class="rounded-sm border border-slate-200 bg-white p-4"
                    >
                        <div class="mb-4 flex items-start justify-between">
                            <h2 class="text-sm font-semibold">{{ chart.title }}</h2>
                            <ActionMenu v-if="hasAudienceData(chart)">
                                <DropdownMenuItem
                                    class="cursor-pointer gap-2 text-xs"
                                    @click="exportIndividualChart(chart.key, chart.title, 'audience')"
                                >
                                    <ImageIcon class="h-3.5 w-3.5" />
                                    <span>Export as Image</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    class="cursor-pointer gap-2 text-xs"
                                    @click="exportIndividualChartCsv(chart.key, chart.title, 'audience')"
                                >
                                    <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                    <span>Export as CSV</span>
                                </DropdownMenuItem>
                            </ActionMenu>
                        </div>
                        <div v-if="hasAudienceData(chart)">
                            <div class="chart-container h-[280px] w-full p-5">
                                <Pie :data="getAudienceChartData(chart)" :options="pieOptions" />
                            </div>

                            <div class="mt-4 flex flex-col space-y-1 text-sm">
                                <template v-if="expandedAudienceCharts[chart.key]">
                                    <div
                                        v-for="(value, index) in getAudienceFacetValues(chart)"
                                        :key="value.key"
                                        class="group relative flex h-7"
                                    >
                                        <div
                                            class="absolute inset-0 mx-2 rounded-[2px]"
                                            :style="`width: ${value.percentage}%`"
                                            :class="`bg-${tailwindColors[index]}-100`"
                                        />
                                        <div class="relative flex w-full justify-between">
                                            <div class="flex">
                                                <div
                                                    class="mr-2 h-full w-2.5 rounded-[2px]"
                                                    :class="`bg-${tailwindColors[index]}-400`"
                                                />
                                                <div class="my-auto leading-none">
                                                    {{ chart.label ? chart.label(value.key) : value.key }}
                                                </div>
                                            </div>
                                            <div class="my-auto leading-none">
                                                {{ numeral(value.percentage / 100).format('0.0%') }}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        v-if="getAudienceFacetValues(chart).length > 3"
                                        class="mt-2 font-medium text-indigo-500 hover:text-indigo-700"
                                        @click="expandedAudienceCharts[chart.key] = false"
                                    >
                                        Show less
                                    </button>
                                </template>
                                <template v-else>
                                    <div
                                        v-for="(value, index) in getAudienceFacetValues(chart).slice(0, 3)"
                                        :key="value.key"
                                        class="group relative flex h-7"
                                    >
                                        <div
                                            class="absolute inset-0 mx-2 rounded-[2px]"
                                            :style="`width: ${value.percentage}%`"
                                            :class="`bg-${tailwindColors[index]}-100`"
                                        />
                                        <div class="relative flex w-full justify-between">
                                            <div class="flex">
                                                <div
                                                    class="mr-2 h-full w-2.5 rounded-[2px]"
                                                    :class="`bg-${tailwindColors[index]}-400`"
                                                />
                                                <div class="my-auto font-medium leading-none">
                                                    {{ chart.label ? chart.label(value.key) : value.key }}
                                                </div>
                                            </div>
                                            <div class="my-auto leading-none">
                                                {{ numeral(value.percentage / 100).format('0.0%') }}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        v-if="getAudienceFacetValues(chart).length > 3"
                                        class="mt-2 font-medium text-indigo-500 hover:text-indigo-700"
                                        @click="expandedAudienceCharts[chart.key] = true"
                                    >
                                        Show more
                                    </button>
                                </template>
                            </div>
                        </div>

                        <p v-else class="py-4 text-center text-gray-500">No data available for this chart</p>
                    </div>
                </div>

                <div v-if="audienceRiskFactors.length" class="mt-8 border-t border-slate-200 pt-5">
                    <div ref="brandSafetyRef" class="">
                        <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 class="text-base font-semibold text-slate-900">Top Brand Safety Risk Factors</h2>
                                <p class="text-sm text-slate-500">
                                    These risk categories are derived from comments and chat messages by each
                                    creator's audience.
                                </p>
                            </div>
                            <ActionMenu>
                                <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportBrandSafety">
                                    <ImageIcon class="h-3.5 w-3.5" />
                                    <span>Export as Image</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportBrandSafetyCsv">
                                    <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                    <span>Export as CSV</span>
                                </DropdownMenuItem>
                            </ActionMenu>
                        </div>

                        <div class="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                            <div v-for="factor in audienceRiskFactors" :key="factor.key">
                                <GradeCard
                                    :grade="getRiskGradeDetails(factor.percentage).grade"
                                    :title="factor.label"
                                    :description="getRiskGradeDetails(factor.percentage).description"
                                    :color-class="
                                        getRiskGradeDetails(factor.percentage).grade === 'C'
                                            ? 'bg-amber-400'
                                            : getRiskGradeDetails(factor.percentage).grade === 'D'
                                            ? 'bg-orange-400'
                                            : 'bg-red-500'
                                    "
                                    variant="risk"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="audienceScoreCards.length" ref="audienceInsightsRef" class="mt-8 border-t border-slate-200 pt-5">
                    <div class="mb-2 flex items-start justify-between">
                        <div>
                            <h2 class="text-base font-semibold text-slate-800">Audience Insights</h2>
                            <p class="text-sm text-slate-500">
                                Aggregate community health and safety scores based on the creators in this report.
                            </p>
                        </div>
                        <ActionMenu>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportAudienceInsights">
                                <ImageIcon class="h-3.5 w-3.5" />
                                <span>Export as Image</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportAudienceInsightsCsv">
                                <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                <span>Export as CSV</span>
                            </DropdownMenuItem>
                        </ActionMenu>
                    </div>

                    <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <div v-for="card in audienceScoreCards" :key="card.key">
                            <GradeCard
                                :grade="card.grade"
                                :title="card.title"
                                :description="card.description"
                                :color-class="card.colorClass"
                            />
                        </div>
                    </div>
                </div>

                <div v-if="audienceFacetPanels.length" ref="audienceFacetsRef" class="mt-8 border-t border-slate-200 pt-5">
                    <div class="mb-2 flex items-start justify-between">
                        <div>
                            <h2 class="text-base font-semibold text-slate-800">Detailed Audience Profile</h2>
                            <p class="text-sm text-slate-500">
                                Income ranges, education levels, occupations, religious affiliation, and engagement style of
                                the audience.
                            </p>
                        </div>
                        <ActionMenu>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportAudienceFacets">
                                <ImageIcon class="h-3.5 w-3.5" />
                                <span>Export as Image</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportAudienceFacetsCsv">
                                <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                <span>Export as CSV</span>
                            </DropdownMenuItem>
                        </ActionMenu>
                    </div>

                    <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <div
                            v-for="panel in audienceFacetPanels"
                            :id="`audience-facet-${panel.key}`"
                            :key="panel.key"
                            class="flex flex-col rounded-sm border border-slate-200 bg-white"
                        >
                            <header class="flex items-start justify-between border-b border-slate-100 px-5 py-4">
                                <div>
                                    <h2 class="text-sm font-semibold text-slate-800">{{ panel.title }}</h2>
                                    <p v-if="panel.description" class="text-xs text-slate-500">{{ panel.description }}</p>
                                </div>
                                <ActionMenu>
                                    <DropdownMenuItem
                                        class="cursor-pointer gap-2 text-xs"
                                        @click="exportAudienceFacet(panel.key, panel.title)"
                                    >
                                        <ImageIcon class="h-3.5 w-3.5" />
                                        <span>Export as Image</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        class="cursor-pointer gap-2 text-xs"
                                        @click="exportAudienceFacetCsv(panel.key, panel.title)"
                                    >
                                        <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                        <span>Export as CSV</span>
                                    </DropdownMenuItem>
                                </ActionMenu>
                            </header>
                            <div class="grow p-4">
                                <ul class="mb-4 space-y-1 text-sm text-slate-800">
                                    <li
                                        v-for="(item, index) in getAudienceFacetItems(panel.items, panel.key)"
                                        :key="item.key"
                                        class="group relative flex h-8 items-center overflow-hidden rounded px-1.5"
                                    >
                                        <div
                                            class="absolute inset-0 mx-2 rounded-[2px]"
                                            :class="`bg-${getColorNameByIndex(index)}-100`"
                                            :style="{ width: `${clampPercentage(item.percentage)}%` }"
                                            aria-hidden="true"
                                        />
                                        <div class="relative flex w-full justify-between gap-2">
                                            <div class="flex h-full gap-2">
                                                <div
                                                    class="h-8 w-2.5 rounded-[2px]"
                                                    :class="`bg-${getColorNameByIndex(index)}-400`"
                                                />
                                                <div class="flex items-center font-medium text-slate-700">
                                                    {{ item.label }}
                                                </div>
                                            </div>
                                            <div class="font-medium text-slate-900">
                                                {{ numeral(item.percentage / 100).format('0.0%') }}
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                                <button
                                    v-if="panel.items.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
                                    @click="toggleAudienceFacetSection(panel.key)"
                                >
                                    {{
                                        audienceFacetSections[panel.key]
                                            ? 'Show Less'
                                            : `Show ${panel.items.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- We do this because Tailwind won't load in colors that are dynamically referenced -->
                <div class="hidden">
                    <div
                        class="bg-blue-100 bg-cyan-100 bg-green-100 bg-lime-100 bg-orange-100 bg-pink-100 bg-purple-100 bg-red-100 bg-teal-100 bg-yellow-100"
                    ></div>
                    <div
                        class="bg-blue-400 bg-cyan-400 bg-green-400 bg-lime-400 bg-orange-400 bg-pink-400 bg-purple-400 bg-red-400 bg-teal-400 bg-yellow-400"
                    ></div>
                </div>
            </div>

        </template>

    </div>
</template>

<script setup>
import _ from 'lodash'
import voca from 'voca'
import moment from 'moment'
import numeral from 'numeral'
import { computed, ref, watch } from 'vue'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ActionMenu, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Badge, GradeIndicator } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { GradeCard } from '@/components/ui/card'

import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command'
import {
    Chart,
    ArcElement,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    CategoryScale,
    TimeScale,
    BarController,
    BarElement,
} from 'chart.js'
import { Pie, Line } from 'vue-chartjs'
import { tailwindConfig, hexToRGB } from '@/utils/Utils'
import { Check, ChevronDown, Image as ImageIcon } from 'lucide-vue-next'
import { useCsvExport } from '@/composables/useCsvExport'
import { useImageExport } from '@/composables/useImageExport'
import { usePdfExport } from '@/composables/usePdfExport'
import SponsorshipAnalyticsTables from '@/partials/charts/SponsorshipAnalyticsTables.vue'

Chart.register(
    ArcElement,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    CategoryScale,
    TimeScale,
    BarController,
    BarElement,
)

const props = defineProps({
    aggregationData: {
        type: Object,
        required: false,
    },
    organicTimeseriesData: {
        type: Object,
        required: false,
    },
    sponsoredTimeseriesData: {
        type: Object,
        required: false,
    },
    facets: {
        type: Object,
        required: false,
    },
    summary: {
        type: Object,
        required: false,
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    isLoadingFacets: {
        type: Boolean,
        default: false,
    },
    selectedFacetDimension: {
        type: String,
        default: 'all',
    },
    selectedFacetKey: {
        type: String,
        default: 'all',
    },
    selectedFacetLabel: {
        type: String,
        default: 'All',
    },
    creators: {
        type: Array,
        default: () => [],
    },
    sponsorshipEnabled: {
        type: Boolean,
        default: false,
    },
    reportId: {
        type: String,
        default: null,
    },
})

const summaryMetrics = computed(() => {
    const fields = [
        {
            key: 'unique_creators_count',
            label: 'Creators',
            description: 'Unique creators in this report.',
            format: (value) => numeral(value).format('0,0'),
            color: 'emerald',
        },
        {
            key: 'sum_content',
            label: 'Content Items',
            description: 'Videos, streams, and posts included.',
            format: (value) => numeral(value).format('0,0'),
            color: 'blue',
        },
        {
            key: 'sum_duration_seconds',
            label: 'Hours Streamed',
            description: 'Total live and recorded hours.',
            format: (value) => numeral(value / 3600).format('0,0'),
            color: 'purple',
        },
        {
            key: 'sum_minutes_watched',
            label: 'Hours Watched',
            description: 'Total audience watch time.',
            format: (value) => numeral(value / 60).format('0,0'),
            color: 'amber',
        },
        {
            key: 'sum_views',
            label: 'Views',
            description: 'Total views from all creators.',
            format: (value) => numeral(value).format('0,0'),
            color: 'rose',
        },
        {
            key: 'sum_likes',
            label: 'Likes',
            description: 'Total likes from all creators.',
            format: (value) => numeral(value).format('0,0'),
            color: 'teal',
        },
        {
            key: 'sum_comments',
            label: 'Comments',
            description: 'Total comments from all creators.',
            format: (value) => numeral(value).format('0,0'),
            color: 'cyan',
        },
        {
            key: 'sum_estimated_content_value',
            label: 'Estimated Content Value',
            description: 'Estimated market value of this report.',
            format: (value) => numeral(value).format('$0,0'),
            color: 'indigo',
        },
    ]

    _.forEach(fields, (field) => {
        field.source_value = props.summary[field.key]

        if (_.has(props.summary, field.key)) {
            field.value = field.format(field.source_value)
            field.is_enabled = true
        }
    })

    return _.filter(fields, (item) => item.is_enabled)
})

const chartContainer = ref(null)
const activeTab = ref('weekly')
const metricMenuOpen = ref(false)
const activeMetricTab = ref('report_totals')
const selectedMetricIds = ref([])
const resolvedFacetDimension = computed(() => props.selectedFacetDimension || 'all')
const resolvedFacetKey = computed(() => props.selectedFacetKey || 'all')
const resolvedFacetLabel = computed(() => props.selectedFacetLabel || 'All')

// Image export functionality - refs for each section
const keyMetricsRef = ref(null)
const chartCardRef = ref(null)
const channelDemographicsRef = ref(null)
const audienceDemographicsRef = ref(null)
const audienceInsightsRef = ref(null)
const audienceFacetsRef = ref(null)
const brandSafetyRef = ref(null)

const { downloadAsImage } = useImageExport()
const { downloadAsCsv } = useCsvExport()
const { generatePdfFromSections } = usePdfExport()

const downloadPdf = async (filename, onProgress) => {
    const sections = [
        keyMetricsRef.value,
        chartCardRef.value,
        channelDemographicsRef.value,
        audienceDemographicsRef.value,
        // audienceInsightsRef is nested inside audienceDemographicsRef, so don't include it separately
        audienceFacetsRef.value,
        brandSafetyRef.value,
    ].filter((el) => !!el)

    await generatePdfFromSections(sections, filename, {
        title: props.selectedFacetLabel ? `${filename} - ${props.selectedFacetLabel}` : filename,
        onProgress,
    })
}

defineExpose({
    downloadPdf,
})

// Export functions for each section
const exportKeyMetrics = async () => {
    if (keyMetricsRef.value) {
        try {
            await downloadAsImage(keyMetricsRef.value, 'key-metrics')
        } catch (error) {
            console.error('Failed to export key metrics:', error)
        }
    }
}

const exportChartCard = async () => {
    if (chartCardRef.value) {
        try {
            await downloadAsImage(chartCardRef.value, 'content-timeline')
        } catch (error) {
            console.error('Failed to export chart:', error)
        }
    }
}

const exportChannelDemographics = async () => {
    if (channelDemographicsRef.value) {
        try {
            await downloadAsImage(channelDemographicsRef.value, 'channel-demographics')
        } catch (error) {
            console.error('Failed to export channel demographics:', error)
        }
    }
}

const exportAudienceDemographics = async () => {
    if (audienceDemographicsRef.value) {
        try {
            await downloadAsImage(audienceDemographicsRef.value, 'audience-demographics')
        } catch (error) {
            console.error('Failed to export audience demographics:', error)
        }
    }
}

const exportAudienceInsights = async () => {
    if (audienceInsightsRef.value) {
        try {
            await downloadAsImage(audienceInsightsRef.value, 'audience-insights')
        } catch (error) {
            console.error('Failed to export audience insights:', error)
        }
    }
}

const exportAudienceFacets = async () => {
    if (audienceFacetsRef.value) {
        try {
            await downloadAsImage(audienceFacetsRef.value, 'audience-demographics-detailed')
        } catch (error) {
            console.error('Failed to export audience facets:', error)
        }
    }
}

const exportBrandSafety = async () => {
    if (brandSafetyRef.value) {
        try {
            await downloadAsImage(brandSafetyRef.value, 'brand-safety-risk-factors')
        } catch (error) {
            console.error('Failed to export brand safety:', error)
        }
    }
}

const exportAudienceFacet = async (facetKey, title) => {
    const facetElement = document.getElementById(`audience-facet-${facetKey}`)
    if (facetElement) {
        try {
            await downloadAsImage(facetElement, `audience-${facetKey}`)
        } catch (error) {
            console.error(`Failed to export ${title}:`, error)
        }
    }
}

const exportIndividualChart = async (chartKey, chartTitle, type = 'channel') => {
    const chartElement = document.getElementById(`${type}-chart-${chartKey}`)
    if (chartElement) {
        try {
            await downloadAsImage(chartElement, `${type}-${chartKey}`)
        } catch (error) {
            console.error(`Failed to export ${chartTitle}:`, error)
        }
    }
}

const tailwindColors = ref([
    'blue',
    'green',
    'red',
    'purple',
    'pink',
    'yellow',
    'orange',
    'cyan',
    'teal',
    'lime',
    'emerald',
    'sky',
    'violet',
    'fuchsia',
])

const themeColors = tailwindConfig().theme.colors

const fallbackBlueShade = (shade = 400) => {
    const bluePalette = themeColors.blue
    if (typeof bluePalette === 'string') {
        return bluePalette
    }
    return bluePalette?.[shade] || bluePalette?.[400] || '#60a5fa'
}

const getColorNameByIndex = (index = 0) => {
    if (!Array.isArray(tailwindColors.value) || tailwindColors.value.length === 0) {
        return 'blue'
    }
    return tailwindColors.value[index % tailwindColors.value.length]
}

const getTailwindColorValue = (colorName, shade = 400) => {
    const palette = themeColors[colorName]
    if (!palette) {
        return fallbackBlueShade(shade)
    }
    if (typeof palette === 'string') {
        return palette
    }
    return palette[shade] || fallbackBlueShade(shade)
}

const getPaletteColorByIndex = (index = 0, shade = 400) => {
    const colorName = getColorNameByIndex(index)
    return getTailwindColorValue(colorName, shade)
}

const clampPercentage = (value) => {
    const numericValue = typeof value === 'number' ? value : Number(value) || 0
    return Math.min(Math.max(numericValue, 0), 100)
}

const gradeAccentColors = {
    A: 'emerald',
    B: 'blue',
    C: 'amber',
    D: 'rose',
    F: 'rose',
}

const getGradeAccentColor = (grade, shade = 200) => {
    const colorName = gradeAccentColors[grade] || 'blue'
    return getTailwindColorValue(colorName, shade)
}

const riskGradeText = {
    A: { label: 'Minimal Risk', descriptor: 'Virtually no brand-safety issues detected.' },
    B: { label: 'Low Risk', descriptor: 'Occasional risk language, generally safe.' },
    C: { label: 'Moderate Risk', descriptor: 'Emerging risk signals worth monitoring.' },
    D: { label: 'High Risk', descriptor: 'Frequent safety concerns require attention.' },
    F: { label: 'Critical Risk', descriptor: 'Severe and persistent safety issues.' },
}

const getRiskGradeDetails = (percentage = 0) => {
    const pct = clampPercentage(percentage)
    const adjustedScore = 100 - pct
    const grade = gradeFromScore(adjustedScore)
    const text = riskGradeText[grade] || riskGradeText.C

    const colors = {
        badgeBg: getGradeAccentColor(grade, 100),
        badgeText: getGradeAccentColor(grade, 700),
        border: getGradeAccentColor(grade, 200),
        track: getGradeAccentColor(grade, 50),
        solid: getGradeAccentColor(grade, 500),
    }

    return {
        grade,
        percentage: pct,
        label: text.label,
        description: text.descriptor,
        colors,
    }
}

const expandedChannelCharts = ref({})
const expandedAudienceCharts = ref({})
const audienceFacetSections = ref({})

const countryLookup = new Intl.DisplayNames(undefined, { type: 'region' })
const languageLookup = new Intl.DisplayNames(undefined, { type: 'language' })

const channelPieCharts = [
    {
        title: 'Language Distribution',
        key: 'by_language',
        label: (value) => {
            try {
                return languageLookup?.of(value)
            } catch (error) {
                return 'Unknown'
            }
        },
    },
    {
        title: 'Country Distribution',
        key: 'by_country',
        label: (value) => {
            try {
                return countryLookup?.of(value)
            } catch (error) {
                return 'Unknown'
            }
        },
    },
    {
        title: 'Gender Distribution',
        key: 'by_gender',
        label: (value) => voca.capitalize(value),
    },
    {
        title: 'Age Distribution',
        key: 'by_age_range',
        label: (value) => voca.capitalize(value),
    },
    {
        title: 'Follower Distribution',
        key: 'by_followers',
        label: (value) => {
            switch (value) {
                case 'less_than_1000':
                    return '<1K'
                case '1000_plus':
                    return '1K-10K'
                case '10000_plus':
                    return '10K-100K'
                case '100000_plus':
                    return '100K-1M'
                case '1000000_plus':
                    return '1M+'
                default:
                    return value
            }
        },
    },
    {
        title: 'Channel Type Distribution',
        key: 'by_channel_type',
        label: (value) => voca.titleCase(String(value || '').replace(/_/g, ' ')),
    },
]

const audienceFacetConfigs = [
    {
        key: 'audience_income',
        title: 'Audience Income Ranges',
        description: 'Estimated household income ranges across the audience.',
    },
    {
        key: 'audience_education',
        title: 'Audience Education Levels',
        description: 'Highest level of education inferred for audience members.',
    },
    {
        key: 'audience_occupation',
        title: 'Audience Occupations',
        description: 'Most common professions represented within the audience.',
    },
    {
        key: 'audience_engagement_style',
        title: 'Audience Engagement Style',
        description: 'How the audience typically participates in communities.',
    },
]

const audiencePieCharts = [
    {
        title: 'Audience Language',
        key: 'audience_language',
        label: (value) => {
            try {
                return languageLookup?.of(value)
            } catch (error) {
                return 'Unknown'
            }
        },
    },
    {
        title: 'Audience Country',
        key: 'audience_country',
        label: (value) => {
            try {
                return countryLookup?.of(value)
            } catch (error) {
                return 'Unknown'
            }
        },
    },
    { title: 'Audience Gender', key: 'audience_gender', label: (value) => voca.capitalize(value) },
    { title: 'Audience Age Range', key: 'audience_age_range', label: (value) => value },
    {
        title: 'Audience Ethnicity',
        key: 'audience_ethnicity',
        label: (value) => voca.titleCase(value.replace(/_/g, ' ')),
    },
    {
        title: 'Audience Family Status',
        key: 'audience_family_status',
        label: (value) => voca.titleCase(value.replace(/_/g, ' ')),
    },
]

const hasChannelData = (chart) => {
    const facet = props.aggregationData?.[chart.key]
    if (!facet) return false
    return Object.keys(facet).length > 0
}

const hasAudienceData = (chart) => {
    const facet = props.aggregationData?.[chart.key]
    if (!facet) return false
    return Object.keys(facet).length > 0
}

const pieOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
            position: 'bottom',
        },
        tooltip: {
            callbacks: {
                label: (context) => {
                    const label = context.label || ''
                    const percentage = Number(context.parsed)
                    const formatted = Number.isFinite(percentage) ? percentage.toFixed(1) : '0.0'
                    return `${label}: ${formatted}%`
                },
            },
        },
    },
}))

const getChannelFacetValues = (chart) => {
    const facet = props.aggregationData?.[chart.key]
    if (!facet) return []
    const entries = Object.entries(facet).filter(([key]) => !key.startsWith('_'))
    if (entries.length === 0) return []

    const values = entries.map(([key, metrics]) => {
        const rawCount =
            metrics.unique_creators_count ??
            (Array.isArray(metrics.unique_creator_ids) ? metrics.unique_creator_ids.length : 0)
        return {
            key,
            count: rawCount,
            metrics,
        }
    })

    const totalCreators = values.reduce((sum, item) => sum + item.count, 0)

    const valuesWithPercentages = values.map((item) => {
        let normalizedKey = item.key
        if (chart.key === 'by_followers') {
            if (normalizedKey === '< 1000') {
                normalizedKey = 'less_than_1000'
            } else if (normalizedKey === '1000 - 10000') {
                normalizedKey = '1000_plus'
            } else if (normalizedKey === '10000 - 100000') {
                normalizedKey = '10000_plus'
            } else if (normalizedKey === '100000 - 1000000') {
                normalizedKey = '100000_plus'
            } else if (normalizedKey === '> 1000000') {
                normalizedKey = '1000000_plus'
            }
        }
        return {
            ...item,
            key: normalizedKey,
            percentage: totalCreators > 0 ? (item.count / totalCreators) * 100 : 0,
        }
    })

    return _.orderBy(valuesWithPercentages, ['percentage'], ['desc'])
}

const getAudienceFacetValues = (chart) => {
    const facet = props.aggregationData?.[chart.key]
    if (!facet) return []
    const entries = Object.entries(facet).filter(([key]) => !key.startsWith('_'))
    if (entries.length === 0) return []

    const values = entries
        .map(([key, metrics]) => ({
            key,
            percentage: Number(metrics?.percentage ?? 0),
            count: metrics?.creator_count ?? 0,
            metrics,
        }))
        .filter((item) => item.percentage > 0)

    return _.orderBy(values, ['percentage'], ['desc'])
}

const toggleAudienceFacetSection = (key) => {
    audienceFacetSections.value[key] = !audienceFacetSections.value[key]
}

const getAudienceFacetItems = (items, key, limit = 5) => {
    if (!items) return []
    if (audienceFacetSections.value[key]) return items
    return items.slice(0, limit)
}

const getChannelChartData = (chart) => {
    const sortedValues = getChannelFacetValues(chart).slice(0, 10)

    const data = sortedValues.map((item) => item.percentage)
    const labels = sortedValues.map((item) => (chart.label ? chart.label(item.key) : item.key))

    return {
        labels,
        datasets: [
            {
                data,
                backgroundColor: _(tailwindColors.value)
                    .map((color) => tailwindConfig().theme.colors[color][400])
                    .value(),
            },
        ],
    }
}

const getAudienceChartData = (chart) => {
    const sortedValues = getAudienceFacetValues(chart).slice(0, 10)

    const data = sortedValues.map((item) => item.percentage)
    const labels = sortedValues.map((item) => (chart.label ? chart.label(item.key) : item.key))

    return {
        labels,
        datasets: [
            {
                data,
                backgroundColor: _(tailwindColors.value)
                    .map((color) => tailwindConfig().theme.colors[color][400])
                    .value(),
            },
        ],
    }
}

const formatMetric = (value, type) => {
    switch (type) {
        case 'percentage':
            return `${numeral(value).format('0.00')}%`
        case 'decimal':
            return numeral(value).format('0.00')
        case 'large':
            return numeral(value).format('0.0a').toUpperCase()
        default:
            return numeral(value).format('0,0')
    }
}

const audienceFacetPanels = computed(() => {
    if (!props.aggregationData) return []

    return audienceFacetConfigs
        .map((config) => {
            const facet = props.aggregationData?.[config.key]
            if (!facet) return null

            const values = Object.entries(facet)
                .filter(([key]) => !key.startsWith('_'))
                .map(([key, metrics]) => ({
                    key,
                    label: config.label ? config.label(key) : voca.titleCase(key.replace(/_/g, ' ')),
                    percentage: Number(metrics?.percentage ?? 0),
                }))
                .filter((item) => item.percentage > 0)

            if (values.length === 0) return null

            return {
                ...config,
                items: _.orderBy(values, ['percentage'], ['desc']),
            }
        })
        .filter(Boolean)
})

const gradePalette = {
    A: 'bg-emerald-50 text-emerald-700',
    B: 'bg-blue-50 text-blue-700',
    C: 'bg-amber-50 text-amber-700',
    D: 'bg-rose-50 text-rose-700',
    F: 'bg-rose-50 text-rose-700',
}

const gradeColorClasses = {
    A: 'bg-emerald-500',
    B: 'bg-blue-500',
    C: 'bg-amber-400',
    D: 'bg-rose-500',
    F: 'bg-rose-500',
}

const letterGrades = {
    A: 'Excellent',
    B: 'Good',
    C: 'Fair',
    D: 'Needs Attention',
    F: 'Critical',
}

const audienceScoreCards = computed(() => {
    const cards = []

    const communityScores = props.facets?.audience_community_health_scores
    if (communityScores) {
        const loyalty = communityScores.loyalty_score?.value ?? null
        const cohesion = communityScores.cohesion_score?.value ?? null
        const toxicity = communityScores.toxicity_score?.value ?? null

        if (loyalty !== null) {
            const loyaltyPercent = loyalty > 1 ? loyalty : loyalty * 100
            const grade = gradeFromScore(loyaltyPercent)
            cards.push({
                key: 'loyalty_score',
                title: 'Community Loyalty',
                grade,
                value: `${numeral(loyaltyPercent).format('0.0')}%`,
                palette: gradePalette[grade],
                colorClass: gradeColorClasses[grade] || 'bg-slate-500',
                description: 'Measures how often viewers return and engage across multiple streams or videos.',
            })
        }

        if (cohesion !== null) {
            const cohesionPercent = cohesion > 1 ? cohesion : cohesion * 100
            const grade = gradeFromScore(cohesionPercent)
            cards.push({
                key: 'cohesion_score',
                title: 'Community Cohesion',
                grade,
                value: `${numeral(cohesionPercent).format('0.0')}%`,
                palette: gradePalette[grade],
                colorClass: gradeColorClasses[grade] || 'bg-slate-500',
                description: 'Measures how actively the audience interacts with each other, not just the creator.',
            })
        }

        if (toxicity !== null) {
            const toxicityPercent = toxicity > 1 ? toxicity : toxicity * 100
            const inverted = 100 - toxicityPercent
            const grade = gradeFromScore(inverted)
            cards.push({
                key: 'toxicity_score',
                title: 'Toxicity Detection',
                grade,
                value: `${numeral(inverted).format('0.0')}%`,
                palette: gradePalette[grade],
                colorClass: gradeColorClasses[grade] || 'bg-slate-500',
                description: 'Percentage of chat or comments free from toxic language and harmful behavior.',
            })
        }
    }

    const brandSafety = props.facets?.audience_brand_safety
    if (brandSafety?.overall_risk_level?.value !== undefined) {
        const riskPercent =
            brandSafety.overall_risk_level.value > 1
                ? brandSafety.overall_risk_level.value
                : brandSafety.overall_risk_level.value * 100
        const invertedRisk = 100 - riskPercent
        const grade = gradeFromScore(invertedRisk)
        cards.push({
            key: 'brand_safety',
            title: 'Brand Safety',
            grade,
            value: `${numeral(invertedRisk).format('0.0')}%`,
            palette: gradePalette[grade],
            colorClass: gradeColorClasses[grade] || 'bg-slate-500',
            description: 'Overall brand safety score based on content themes, language, and audience behavior.',
        })
    }

    return cards
})

const gradeFromScore = (score) => {
    if (score >= 85) return 'A'
    if (score >= 70) return 'B'
    if (score >= 55) return 'C'
    if (score >= 40) return 'D'
    return 'F'
}

const audienceRiskFactors = computed(() => {
    const riskFacet = props.facets?.audience_brand_safety?.risk_factors
    if (!riskFacet) return []

    const entries = Object.entries(riskFacet)
        .filter(([key]) => !key.startsWith('_'))
        .map(([key, metrics]) => ({
            key,
            label: voca.titleCase(key.replace(/_/g, ' ')),
            percentage: Number(metrics?.percentage ?? 0),
        }))
        // Only show items with risk grade C or worse (percentage > 30)
        // A/B grades indicate low/no risk and shouldn't appear as "risk factors"
        .filter((item) => item.percentage > 30)

    return _.orderBy(entries, ['percentage'], ['desc']).slice(0, 5)
})

// In new structure, timeseries are arrays directly on facet values
// Use facets if available, otherwise fall back to organic/sponsored props
const organicDataSeries = computed(() => {
    if (!props.organicTimeseriesData) return []
    if (Array.isArray(props.organicTimeseriesData)) {
        return props.organicTimeseriesData
    }
    return props.organicTimeseriesData?.[activeTab.value] || []
})

const sponsoredDataSeries = computed(() => {
    if (!props.sponsoredTimeseriesData) return []
    if (Array.isArray(props.sponsoredTimeseriesData)) {
        return props.sponsoredTimeseriesData
    }
    return props.sponsoredTimeseriesData?.[activeTab.value] || []
})

const selectedFacetNode = computed(() => {
    if (!props.facets) return null
    if (resolvedFacetDimension.value === 'all') {
        return props.facets.all?.all || null
    }

    return _.get(props.facets, [resolvedFacetDimension.value, resolvedFacetKey.value]) || null
})

// Get available facet values based on selected dimension
// Get timeseries data for a specific facet value
const getFacetTimeseriesData = (facetDimension, facetValue, interval = 'default') => {
    if (!props.facets || !facetDimension || !facetValue) return []

    let facetData = props.facets[facetDimension]?.[facetValue]
    if (facetDimension === 'all' && facetData?.all) {
        facetData = facetData.all
    }
    if (!facetData || !facetData.timeseries) return []

    const timeseries = facetData.timeseries

    // If timeseries is already an array, return it directly
    if (Array.isArray(timeseries)) {
        return timeseries
    }

    // If timeseries is an object keyed by interval (daily/weekly), pick interval
    if (timeseries && typeof timeseries === 'object') {
        if (Array.isArray(timeseries[interval])) {
            return timeseries[interval]
        }
        // Fall back to the first available interval
        const firstInterval = Object.values(timeseries).find((value) => Array.isArray(value))
        if (Array.isArray(firstInterval)) {
            return firstInterval
        }
    }

    return []
}

const essentialMetricDefinitions = [
    { value: 'sum_views', label: 'Views', type: 'sum' },
    { value: 'sum_likes', label: 'Likes', type: 'sum' },
    { value: 'sum_comments', label: 'Comments', type: 'sum' },
    { value: 'sum_shares', label: 'Shares', type: 'sum' },
    { value: 'sum_content', label: 'Content Count', type: 'sum' },
    { value: 'sum_minutes_watched', label: 'Minutes Watched', type: 'sum' },
    { value: 'sum_estimated_content_value', label: 'Content Value', type: 'currency' },
]

const getMetricDefinition = (metricValue) => {
    return (
        essentialMetricDefinitions.find((metric) => metric.value === metricValue) || {
            value: metricValue,
            label: metricValue,
            type: 'sum',
        }
    )
}

const formatMetricValue = (value, metricValue) => {
    const metricDef = getMetricDefinition(metricValue)
    if (metricDef.type === 'currency') {
        return numeral(value).format('$0,0')
    }
    return numeral(value).format('0,0')
}

const hasMetricInTimeseries = (timeseries, metricKey) => {
    if (!Array.isArray(timeseries) || !timeseries.length) return false
    return timeseries.some(
        (entry) => entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, metricKey),
    )
}

const formatFacetLabel = (dimension, key) => {
    if (dimension === 'by_platform') {
        if (key === 'youtube') return 'YouTube'
        if (key === 'tiktok') return 'TikTok'
        if (key === 'twitter') return 'X'
        return _.startCase(key)
    }
    if (dimension === 'by_content_type') {
        return _.startCase(String(key).replace(/_/g, ' '))
    }
    if (dimension === 'by_sponsored') {
        return key === 'true' ? 'Sponsored' : 'Organic'
    }
    return _.startCase(key)
}

const createOptionsFromTimeseries = ({
    timeseries,
    facetDimension,
    facetKey,
    facetLabel,
    groupId,
    source = 'facet',
}) => {
    if (!Array.isArray(timeseries) || timeseries.length === 0) {
        return []
    }

    return essentialMetricDefinitions
        .filter((metric) => hasMetricInTimeseries(timeseries, metric.value))
        .map((metric) => {
            const idParts = [groupId || facetDimension, facetKey, metric.value].filter(Boolean)
            const optionId = idParts.join('::')
            const displayLabel = `${facetLabel} ${metric.label}`
            return {
                id: optionId,
                groupId: groupId || facetDimension,
                facetDimension,
                facetKey,
                facetLabel,
                metricKey: metric.value,
                metricLabel: metric.label,
                metricType: metric.type,
                source,
                shortLabel: displayLabel,
                displayLabel,
            }
        })
}

const buildFacetSubgroups = (dimension, groupId) => {
    if (!props.facets || !props.facets[dimension]) return []
    return Object.entries(props.facets[dimension])
        .filter(([key]) => !String(key).startsWith('_'))
        .map(([key]) => {
            const label = formatFacetLabel(dimension, key)
            const timeseries = getFacetTimeseriesData(dimension, key, activeTab.value)
            const options = createOptionsFromTimeseries({
                timeseries,
                facetDimension: dimension,
                facetKey: key,
                facetLabel: label,
                groupId,
            })
            return {
                id: `${groupId}_${key}`,
                label,
                options,
            }
        })
        .filter((subgroup) => subgroup.options.length > 0)
}

const metricOptionGroups = computed(() => {
    const groups = []

    const totalsOptions = createOptionsFromTimeseries({
        timeseries: getFacetTimeseriesData('all', 'all', activeTab.value),
        facetDimension: 'all',
        facetKey: 'all',
        facetLabel: 'Report Totals',
        groupId: 'report_totals',
    })
    if (totalsOptions.length) {
        groups.push({
            id: 'report_totals',
            label: 'Report Totals',
            subgroups: [
                {
                    id: 'report_totals_all',
                    label: 'All Metrics',
                    options: totalsOptions,
                },
            ],
        })
    }

    const platformSubgroups = buildFacetSubgroups('by_platform', 'platforms')
    if (platformSubgroups.length) {
        groups.push({
            id: 'platforms',
            label: 'Platforms',
            subgroups: platformSubgroups,
        })
    }

    const contentTypeSubgroups = buildFacetSubgroups('by_content_type', 'content_types')
    if (contentTypeSubgroups.length) {
        groups.push({
            id: 'content_types',
            label: 'Content Types',
            subgroups: contentTypeSubgroups,
        })
    }

    let sponsoredSubgroups = buildFacetSubgroups('by_sponsored', 'sponsored_status')
    if (!sponsoredSubgroups.length) {
        const fallbackOrganic = createOptionsFromTimeseries({
            timeseries: organicDataSeries.value,
            facetDimension: 'by_sponsored',
            facetKey: 'false',
            facetLabel: 'Organic',
            groupId: 'sponsored_status',
            source: 'organic',
        })
        const fallbackSponsored = createOptionsFromTimeseries({
            timeseries: sponsoredDataSeries.value,
            facetDimension: 'by_sponsored',
            facetKey: 'true',
            facetLabel: 'Sponsored',
            groupId: 'sponsored_status',
            source: 'sponsored',
        })
        const subgroupsArray = []
        if (fallbackOrganic.length) {
            subgroupsArray.push({
                id: 'sponsored_status_false',
                label: 'Organic',
                options: fallbackOrganic,
            })
        }
        if (fallbackSponsored.length) {
            subgroupsArray.push({
                id: 'sponsored_status_true',
                label: 'Sponsored',
                options: fallbackSponsored,
            })
        }
        sponsoredSubgroups = subgroupsArray
    }
    if (sponsoredSubgroups.length) {
        groups.push({
            id: 'sponsored_status',
            label: 'Sponsored',
            subgroups: sponsoredSubgroups,
        })
    }

    return groups
})

const metricOptionsFlat = computed(() =>
    metricOptionGroups.value.flatMap((group) => group.subgroups.flatMap((subgroup) => subgroup.options)),
)

const metricOptionsIndex = computed(() => {
    const map = new Map()
    metricOptionsFlat.value.forEach((option) => {
        map.set(option.id, option)
    })
    return map
})

const selectedMetricOptions = computed(() =>
    selectedMetricIds.value.map((id) => metricOptionsIndex.value.get(id)).filter(Boolean),
)

const getDefaultMetricOptionId = () => {
    const preferredOption = metricOptionsFlat.value.find(
        (option) =>
            option.facetDimension === resolvedFacetDimension.value &&
            option.facetKey === resolvedFacetKey.value &&
            option.metricKey === 'sum_views',
    )
    if (preferredOption) return preferredOption.id

    const totalsViewsOption = metricOptionsFlat.value.find(
        (option) => option.facetDimension === 'all' && option.facetKey === 'all' && option.metricKey === 'sum_views',
    )
    if (totalsViewsOption) return totalsViewsOption.id

    return metricOptionsFlat.value[0]?.id || null
}

watch(
    () => metricOptionGroups.value,
    () => {
        if (
            metricOptionGroups.value.length > 0 &&
            !metricOptionGroups.value.some((g) => g.id === activeMetricTab.value)
        ) {
            activeMetricTab.value = metricOptionGroups.value[0].id
        }
    },
    { immediate: true },
)

watch(
    () => metricOptionsIndex.value,
    () => {
        const availableIds = new Set(metricOptionsIndex.value.keys())
        const filtered = selectedMetricIds.value.filter((id) => availableIds.has(id))
        if (filtered.length !== selectedMetricIds.value.length) {
            selectedMetricIds.value = filtered
        }
        if (selectedMetricIds.value.length === 0) {
            const defaultOptionId = getDefaultMetricOptionId()
            if (defaultOptionId) {
                selectedMetricIds.value = [defaultOptionId]
            }
        }
    },
    { immediate: true },
)

const isMetricSelected = (optionId) => selectedMetricIds.value.includes(optionId)

const toggleMetricSelection = (optionId) => {
    if (!optionId || !metricOptionsIndex.value.has(optionId)) return

    const current = [...selectedMetricIds.value]
    const existingIndex = current.indexOf(optionId)
    if (existingIndex > -1) {
        current.splice(existingIndex, 1)
    } else {
        current.push(optionId)
    }

    if (!current.length) {
        const fallback = getDefaultMetricOptionId()
        selectedMetricIds.value = fallback ? [fallback] : []
        return
    }

    selectedMetricIds.value = current
}

const clearMetricSelections = () => {
    const fallback = getDefaultMetricOptionId()
    if (fallback) {
        selectedMetricIds.value = [fallback]
    } else {
        selectedMetricIds.value = []
    }
}

// Get available timeseries intervals
const timeseriesIntervals = computed(() => {
    const selectedTimeseries = selectedFacetNode.value?.timeseries
    if (selectedTimeseries && !Array.isArray(selectedTimeseries)) {
        return Object.keys(selectedTimeseries).filter((key) => isNaN(Number(key)) && key !== 'length')
    }
    if (props.organicTimeseriesData && !Array.isArray(props.organicTimeseriesData)) {
        return Object.keys(props.organicTimeseriesData).filter((key) => isNaN(Number(key)) && key !== 'length')
    }
    return []
})

const hasTimeseriesIntervals = computed(() => {
    return timeseriesIntervals.value.length > 0
})

// Watch for facet dimension changes to reset active tab if needed
watch(
    () => ({
        dimension: resolvedFacetDimension.value,
        key: resolvedFacetKey.value,
        intervalsKey: timeseriesIntervals.value.join(','),
    }),
    () => {
        if (timeseriesIntervals.value.length === 0) {
            activeTab.value = 'weekly'
            return
        }

        if (!timeseriesIntervals.value.includes(activeTab.value)) {
            activeTab.value = timeseriesIntervals.value[0]
        }
    },
    { immediate: true },
)

const getOptionTimeseries = (option) => {
    if (!option) return []
    if (option.source === 'organic') {
        return organicDataSeries.value
    }
    if (option.source === 'sponsored') {
        return sponsoredDataSeries.value
    }
    return getFacetTimeseriesData(option.facetDimension, option.facetKey, activeTab.value)
}

const selectedOptionTimeseries = computed(() =>
    selectedMetricOptions.value.map((option) => ({
        option,
        timeseries: getOptionTimeseries(option) || [],
    })),
)

const normalizeTimestamp = (value) => {
    if (!value) return null
    if (typeof value === 'string') return value
    if (value instanceof Date) return value.toISOString()
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed.toISOString()
}

// Create a merged dataset for chart labels (timestamps)
const mergedTimestamps = computed(() => {
    const timestampSet = new Set()

    selectedOptionTimeseries.value.forEach(({ timeseries }) => {
        if (!Array.isArray(timeseries)) return
        timeseries.forEach((entry) => {
            const normalized = normalizeTimestamp(entry?.timestamp)
            if (normalized) {
                timestampSet.add(normalized)
            }
        })
    })

    if (timestampSet.size === 0) {
        const fallbackSeries = [organicDataSeries.value, sponsoredDataSeries.value]
        fallbackSeries.forEach((series) => {
            if (!Array.isArray(series)) return
            series.forEach((entry) => {
                const normalized = normalizeTimestamp(entry?.timestamp)
                if (normalized) {
                    timestampSet.add(normalized)
                }
            })
        })
    }

    return Array.from(timestampSet).sort()
})

const timeseriesOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        intersect: false,
        mode: 'index',
    },
    plugins: {
        legend: {
            position: 'bottom',
            labels: {
                usePointStyle: true,
            },
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                title: (context) => {
                    if (context.length > 0) {
                        const timestamp = context[0].parsed.x
                        return moment(timestamp).format('MMM D YYYY')
                    }
                    return ''
                },
                label: (context) => {
                    const label = context.dataset.label || ''
                    const value = context.parsed.y
                    const metricKey = context.dataset.metricKey
                    return `${label}: ${formatMetricValue(value, metricKey)}`
                },
            },
        },
    },
    scales: {
        x: {
            type: 'time',
            time: {
                unit: 'day',
                tooltipFormat: 'MMM D YYYY',
                displayFormats: {
                    hour: 'MMM D, h:mm A',
                    day: 'MMM D',
                    week: 'MMM Do, YY',
                },
            },
            grid: {
                display: true,
                drawOnChartArea: false,
                tickLength: 10,
                color: `rgba(${hexToRGB(tailwindConfig().theme.colors.slate[200])}, 0.75)`,
                tickColor: `rgba(${hexToRGB(tailwindConfig().theme.colors.slate[300])}, 1)`,
            },
            ticks: {
                maxRotation: 45,
            },
            offset: true, // Add this for proper bar offsets
        },
        y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
                display: false,
                text: 'Views',
            },
            grid: {
                tickLength: 10,
                color: `rgba(${hexToRGB(tailwindConfig().theme.colors.slate[200])}, 0.75)`,
                tickColor: `rgba(${hexToRGB(tailwindConfig().theme.colors.slate[300])}, 1)`,
            },
            ticks: {
                callback: (value) => formatMetric(value, 'large'),
                autoSkip: true,
                autoSkipPadding: 10,
            },
            min: 0,
            stacked: false,
        },
    },
}))

const timeseriesChartData = computed(() => {
    if (!mergedTimestamps.value || mergedTimestamps.value.length === 0) {
        return { datasets: [], labels: [] }
    }

    const optionSeries = selectedOptionTimeseries.value.filter(
        ({ timeseries }) => Array.isArray(timeseries) && timeseries.length > 0,
    )

    if (!optionSeries.length) {
        return { datasets: [], labels: [] }
    }

    const datasetCount = optionSeries.length
    const categoryPercentage = Math.min(0.9, 0.8)
    const barPercentage = Math.min(0.85, datasetCount > 1 ? 0.7 : 0.9)

    const datasets = optionSeries.map(({ option, timeseries }, index) => {
        const color = getPaletteColorByIndex(index, 500)
        const backgroundColor = `rgba(${hexToRGB(color)}, 0.85)`
        const borderColor = `rgba(${hexToRGB(color)}, 1)`
        const valueByTimestamp = new Map()

        timeseries.forEach((entry) => {
            const normalized = normalizeTimestamp(entry?.timestamp)
            if (!normalized) return
            valueByTimestamp.set(normalized, Number(entry?.[option.metricKey] ?? 0))
        })

        return {
            label: option.displayLabel,
            data: mergedTimestamps.value.map((timestamp) => ({
                x: new Date(timestamp),
                y: valueByTimestamp.get(timestamp) ?? 0,
            })),
            backgroundColor,
            borderColor,
            borderWidth: 1,
            fill: false,
            type: 'bar',
            barPercentage,
            categoryPercentage,
            borderRadius: 3,
            maxBarThickness: 24,
            pointRadius: 0,
            pointHoverRadius: 0,
            order: 1,
            metricKey: option.metricKey,
            metricType: option.metricType,
        }
    })

    return {
        labels: mergedTimestamps.value,
        datasets,
    }
})
const viewsChartData = timeseriesChartData

const getCsvFilename = (...parts) => {
    return _.kebabCase(parts.filter(Boolean).join(' ')) || 'export'
}

const getCsvTitleWithoutDistribution = (title) => {
    return String(title || '').replace(/\s+distribution$/i, '')
}

const formatCsvPercentage = (value) => {
    return numeral(Number(value || 0) / 100).format('0.0%')
}

const getMetricCsvRows = () =>
    summaryMetrics.value.map((field) => ({
        Metric: field.label,
        Value:
            field.key === 'sum_estimated_content_value' && Number.isFinite(Number(field.source_value))
                ? Math.round(Number(field.source_value))
                : field.source_value,
    }))

const getTimelineCsvLabel = (label) => {
    return String(label || '')
}

const getTimelineCsvRows = () => {
    const chartData = viewsChartData.value
    if (!chartData?.labels?.length || !chartData?.datasets?.length) return []

    return chartData.labels.map((timestamp, index) => {
        const row = {
            Date: moment(timestamp).format('YYYY-MM-DD'),
        }

        chartData.datasets.forEach((dataset) => {
            row[getTimelineCsvLabel(dataset.label)] = dataset.data?.[index]?.y ?? 0
        })

        return row
    })
}

const getFacetChartCsvRows = (chart, type) => {
    const values = type === 'channel' ? getChannelFacetValues(chart) : getAudienceFacetValues(chart)

    return values.map((value) => ({
        Section: chart.title,
        Label: chart.label ? chart.label(value.key) : value.key,
        Percentage: formatCsvPercentage(value.percentage),
    }))
}

const getChannelDemographicsCsvRows = () =>
    channelPieCharts.filter(hasChannelData).flatMap((chart) => getFacetChartCsvRows(chart, 'channel'))

const getAudienceDemographicsCsvRows = () =>
    audiencePieCharts.filter(hasAudienceData).flatMap((chart) => getFacetChartCsvRows(chart, 'audience'))

const getAudienceInsightsCsvRows = () =>
    audienceScoreCards.value.map((card) => ({
        Metric: card.title,
        Grade: card.grade,
        Value: card.value,
        Description: card.description,
    }))

const getBrandSafetyCsvRows = () =>
    audienceRiskFactors.value.map((factor) => {
        const riskDetails = getRiskGradeDetails(factor.percentage)

        return {
            'Risk Factor': factor.label,
            Grade: riskDetails.grade,
            Description: riskDetails.description,
        }
    })

const getAudienceFacetCsvRows = (facetKey = null) => {
    const panels = facetKey
        ? audienceFacetPanels.value.filter((panel) => panel.key === facetKey)
        : audienceFacetPanels.value

    return panels.flatMap((panel) =>
        panel.items.map((item) => ({
            Section: panel.title,
            Label: item.label,
            Percentage: formatCsvPercentage(item.percentage),
        })),
    )
}

const exportKeyMetricsCsv = async () => {
    await downloadAsCsv(getMetricCsvRows(), 'key-metrics')
}

const exportChartCardCsv = async () => {
    await downloadAsCsv(getTimelineCsvRows(), 'content-timeline')
}

const exportChannelDemographicsCsv = async () => {
    await downloadAsCsv(getChannelDemographicsCsvRows(), 'channel-demographics')
}

const exportAudienceDemographicsCsv = async () => {
    await downloadAsCsv(getAudienceDemographicsCsvRows(), 'audience-demographics')
}

const exportAudienceInsightsCsv = async () => {
    await downloadAsCsv(getAudienceInsightsCsvRows(), 'audience-insights')
}

const exportAudienceFacetsCsv = async () => {
    await downloadAsCsv(getAudienceFacetCsvRows(), 'detailed-audience-profile')
}

const exportBrandSafetyCsv = async () => {
    await downloadAsCsv(getBrandSafetyCsvRows(), 'brand-safety-risk-factors')
}

const exportAudienceFacetCsv = async (facetKey, title) => {
    await downloadAsCsv(getAudienceFacetCsvRows(facetKey), getCsvFilename(title || facetKey))
}

const exportIndividualChartCsv = async (chartKey, chartTitle, type = 'channel') => {
    const chart = (type === 'channel' ? channelPieCharts : audiencePieCharts).find((item) => item.key === chartKey)
    if (!chart) return

    await downloadAsCsv(
        getFacetChartCsvRows(chart, type),
        getCsvFilename(type, getCsvTitleWithoutDistribution(chartTitle || chartKey)),
    )
}
</script>
